import { useSelector } from 'react-redux';

import { useUrlQueryParam } from 'hooks/useUrlQueryParam';
import {
    WS_KEY_REGISTRATION_ATTEMPTS,
    WS_KEY_REGISTRATION_SOURCE,
} from 'lib/constants';
import { redirect } from 'lib/navigation';
import { removeLocalStorageItem, setLocalStorageItem } from 'lib/webStorage';
import { getAuthFeatureFlags } from 'selectors/authFeatureFlags.selectors';

import { Nullable } from '../../../types/general';
import { RegistrationProjectsSource } from '../types';

import eventTracking from '../../../services/analytics/eventTracking';
import auth from '../../../services/auth';
import logger from '../../../services/logger';
import { VALID_PROJECT_SOURCES } from '../constants';

const EVENT_TRACKING_MAP = {
    edo: 'reg_self_edo',
    edi: 'reg_self_edi',
    kasa: 'reg_self_kasa',
    kep: 'reg_self_cap',
    ttn: 'reg_self_ttn',
};

export type ThirdPartyProviders = 'google' | 'microsoft' | 'apple';

export type RegistrationMethod = 'email' | ThirdPartyProviders;

export type RegistrationSuccessEffect = (
    nextUrl?: string,
    email?: string,
    registrationMethod?: RegistrationMethod,
) => Promise<void>;

export const useRegistrationSuccessEffect = (
    sourceLink: string,
): RegistrationSuccessEffect => {
    const projectSource = useUrlQueryParam('source') as Nullable<
        RegistrationProjectsSource | 'edo'
    >;

    const authFeatureFlags = useSelector(getAuthFeatureFlags);
    const isNewRegistrationFlow = authFeatureFlags.NEW_REGISTRATION_FLOW;

    return async (
        nextUrl = '/app/registration/confirm-email',
        email = '',
        registrationMethod = 'email',
    ) => {
        await auth.sendRegistrationAttempts().catch((error) => {
            logger.error('Post registration attempts data failed', { error });
        });

        if (!isNewRegistrationFlow) {
            setLocalStorageItem(WS_KEY_REGISTRATION_SOURCE, sourceLink);
        }
        removeLocalStorageItem(WS_KEY_REGISTRATION_ATTEMPTS);

        if (isNewRegistrationFlow) {
            if (
                projectSource &&
                [...VALID_PROJECT_SOURCES, 'edo'].includes(projectSource)
            ) {
                eventTracking.sendToGTM({
                    event: 'reg_self',
                    category: registrationMethod,
                    action: email,
                    label: projectSource,
                });
                eventTracking.sendToGTM({
                    event: EVENT_TRACKING_MAP[projectSource],
                    category: registrationMethod,
                    action: email,
                });
            } else {
                eventTracking.sendToGTM({
                    event: 'reg_from_link',
                    category: registrationMethod,
                    action: email,
                });
            }
        }

        if (['google', 'microsoft', 'apple'].includes(registrationMethod)) {
            eventTracking.sendToGTM({
                event: `${registrationMethod}_reg_success`,
                category: `${registrationMethod}_reg_success`,
                action: email,
            });
        }

        redirect(nextUrl);
    };
};
